/**
 * React Query key factory for the chat resource.
 *
 * Mirrors `conversationsKeys` in shape: one root, a list factory keyed by
 * conversation id. Exported so the WS handler (M5-T6) and any future
 * mutation hooks can derive keys from a single source of truth instead of
 * hand-rolling tuples.
 *
 *   ['chat', 'messages', conversationId]
 *   ['chat', 'thread-messages', threadKey]
 */

export const CHAT_ROOT = 'chat' as const;

export const chatKeys = {
  /** Root for `removeQueries({ queryKey: chatKeys.all })`. */
  all: [CHAT_ROOT] as const,

  /** Root for any messages-list query — useful as a prefix. */
  messagesRoot: () => [CHAT_ROOT, 'messages'] as const,

  /** Infinite-list key for one conversation's messages. */
  messages: (conversationId: string) =>
    [CHAT_ROOT, 'messages', conversationId] as const,

  /** Root for any merged-thread messages query — prefix for invalidation
   *  (e.g. `resetRealtimeFedCaches` after a `replay.gap`). Deliberately a
   *  distinct second segment from `messagesRoot()` so per-conversation and
   *  merged-thread windows are separate cache families. */
  threadMessagesRoot: () => [CHAT_ROOT, 'thread-messages'] as const,

  /** Infinite-list key for one merged phone-thread's messages. `threadKey`
   *  is the RAW `COALESCE(phone_e164, peer_id)` grouping key (NOT URL
   *  encoded — encoding is the API layer's job). */
  threadMessages: (threadKey: string) =>
    [CHAT_ROOT, 'thread-messages', threadKey] as const,
};
